import { Form, notification, Select } from "antd";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { coreApi } from "../../api/calls";
import { sortPartners, getPartnerTitle } from "../helpers/sort";
import { setAvailablePartnerStates } from "../../actions";

const AllPartnersForOrdersSelect = ({
  label,
  name,
  initialValue,
  disabledOption,
  readOnly,
  required,
  validationMessage,
  size,
  onChange = null,
  className = "default-item-class",
  disabled = false,
  selectedLocations = [],
  selectedPartnerStates = [],
  onPartnersLoaded = null,
  value = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [allPartners, setAllPartners] = useState([]);
  const [hasLoadedAll, setHasLoadedAll] = useState(false);
  const requestKeyRef = useRef("");

  useEffect(() => {
    if (!selectedLocations || selectedLocations.length === 0) {
      return;
    }

    const requestKey = `${selectedLocations.join(',')}-${selectedPartnerStates.join(',')}`;
    if (requestKeyRef.current === requestKey) {
      return;
    }
    requestKeyRef.current = requestKey;

    let isMounted = true;
    setLoading(true);

    const params = {};
    if (selectedLocations.length > 0) {
      params.locations = selectedLocations;
    }
    if (selectedPartnerStates && selectedPartnerStates.length > 0) {
      params.partnerStates = selectedPartnerStates;
    }
    
    coreApi
      .get("/partners/available-for-orders", { params })
      .then((response) => {
        if (isMounted) {
          const validPartners = response.data.filter(partner => 
            partner && partner.id != null
          );
          
          const uniqueStates = [...new Set(
            validPartners
              .map(partner => partner.state)
              .filter(state => state != null && state !== '')
          )];
          
          dispatch(setAvailablePartnerStates(uniqueStates));
          
          if (onPartnersLoaded) {
            onPartnersLoaded(sortPartners(validPartners));
          }
        }
      })
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message });
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [selectedLocations, selectedPartnerStates, dispatch, onPartnersLoaded]);

  useEffect(() => {
    if (hasLoadedAll) return;
    
    let isMounted = true;
    setLoading(true);
    
    coreApi
      .get("/partners/all-with-locations")
      .then((response) => {
        if (isMounted) {
          setAllPartners(sortPartners(response.data));
          setHasLoadedAll(true);
        }
      })
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message });
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [hasLoadedAll]);

  const handleClear = () => {
    if (onChange) {
      onChange([]);
    }
  };

  const handlePartnerChange = (partnerId) => {
    if (onChange) {
      const selectedPartners = allPartners.filter(partner =>
        partnerId.includes(partner.id)
      );
      onChange(selectedPartners);
    }
  };

  const filterOption = (input, option) => {
    if (typeof option.children === "string") {
      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    return false;
  };

  return (
    <Form.Item
      className={className}
      hasFeedback
      name={name}
      label={label}
      initialValue={initialValue}
      valuePropName="value"
      rules={[
        {
          required: required,
          message: validationMessage,
        },
      ]}
    >
      <Select
        size={size}
        allowClear
        disabled={disabled || readOnly}
        bordered={!readOnly}
        loading={loading}
        showSearch
        mode="multiple"
        placeholder={t("partners.selectAvailablePartner")}
        filterOption={filterOption}
        onChange={handlePartnerChange}
        onClear={handleClear}
        value={value}
      >
        {allPartners.map((partner, index) => (
          <Select.Option
            key={`partner-${partner.id || index}`}
            value={partner.id}
            disabled={disabledOption && partner.id === disabledOption}
          >
            {partner.contract_ended === null ? (
              partner.company_name || getPartnerTitle(partner)
            ) : (
              <s>{partner.company_name || getPartnerTitle(partner)}</s>
            )}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default AllPartnersForOrdersSelect;
